import { useState, useMemo, useId, type CSSProperties } from "react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { WhyDisabled } from "@/components/ui/disabled-reason";
import { useActivity } from "@/hooks/useActivity";
import { useRunEstimate } from "@/hooks/useRunEstimate";
import { formatRange } from "@/lib/formatRange";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  BarChart3,
  BookOpen,
  ChevronDown,
  Download,
  FileDown,
  FilePlus2,
  FileText,
  GitBranch,
  Loader2,
  Network,
  Plus,
  Radio,
  RotateCcw,
  ScanSearch,
  Search,
  Settings2,
  Sparkles,
  Trash2,
  Upload,
} from "lucide-react";
import { TrawlOptionsPopover } from "./TrawlOptionsPopover";
import { MonitorDigest } from "./MonitorDigest";
import { MonitorSettingsPanel } from "./MonitorSettingsPanel";
import { useSnowballStore } from "@/stores/snowball-store";
import { useSelectionStore } from "@/stores/selection-store";
import { getEligibleSeeds } from "@/hooks/useSnowball";
import type { Concept } from "@/types/concept";
import type { PaperWithRole } from "@/types/paper";
import { GLOSSARY } from "@/lib/glossary";

export type FrontierAccent = "blue" | "green" | "lavender" | "yellow" | "mauve";

/** Grouped handler bundles — populated in Tasks 4–5. Kept as objects so the
 *  curate.tsx wiring passes one prop per menu rather than ~25 loose callbacks. */
export interface FindHandlers {
  hasFoundationalPapers: boolean;
  onTrawl: () => void;
  onSmartTrawl: () => void;
  onSnowball: () => void;
  onImportPdfs: () => void;
  onImportBibTeX: () => void;
  onZotero: () => void;
  onRunMonitor: () => void;
  /** "Start over…" — omitted when the viewer isn't in the reset ring. */
  onStartOver?: () => void;
  /** "Undo last reset (<date>)" — present only while the latest reset is
   *  undoable. */
  onUndoReset?: () => void;
  undoResetLabel?: string;
}
export interface ScoreHandlers {
  onScoreAll: () => void;
  onScoreSelected: () => void;
  onRescoreFullText: () => void;
  onResetScores: () => void;
  onBackfillAbstracts: () => void;
  unscoredCount: number;
  needsRescoreCount: number;
  idlePdfCount: number;
  scoringLimit: number | null;
  scoredCount: number;
  isResetPending: boolean;
  /** Opens billing; shown under "Score all" when the scoring limit is used up. */
  onUpgrade?: () => void;
  missingAbstractCount: number;
  isBackfilling: boolean;
  backfillProgress: { total: number; remaining: number } | null;
}
export interface CorpusHandlers {
  onExportCSV: () => void;
  onExportBibTeX: () => void;
  onDownloadPdfs: () => void;
  onDownloadSelectedPdfs: () => void;
}

export interface MonitorHandlers {
  concept: Concept;
  isMonitorRunning: boolean;
  monitorRecentCount: number;
  monitorHighCount: number;
  monitorMidCount: number;
  monitorLowCount: number;
  monitorLastRunCount: number;
  monitorLastRunHighCount: number;
  monitorLastRunMidCount: number;
  monitorLastRunLowCount: number;
  untriagedCount: number;
  onRunMonitor: () => void;
  onCancelMonitor: () => void;
  onOpenTriageInbox: () => void;
  onMonitorSettingsChange: (settings: {
    monitor_enabled?: boolean;
    monitor_interval_days?: number;
    monitor_max_citations?: number;
  }) => void;
  isMonitorSettingsUpdating: boolean;
}

interface ActionBarProps {
  /** `reason` is the advisor's "why now" — shown on hover/focus of the button. */
  frontier: { label: string; accent: FrontierAccent; reason?: string } | null;
  onFrontier: () => void;
  /** "Why?" link next to the button: opens the Where am I map (all next steps). */
  onFrontierWhy?: () => void;
  papers: PaperWithRole[];
  find: FindHandlers;
  score: ScoreHandlers;
  corpus: CorpusHandlers;
  monitor: MonitorHandlers;
  onSearchS2: (q: string, limit?: number) => void;
  /** Defaults to "Search Semantic Scholar…". */
  searchPlaceholder?: string;
  isSearching: boolean;
  busy: boolean;
  /** What `busy` is waiting on, e.g. "PDF retrieval" — dimmed menus say so. */
  busyJob?: string;
}

/** Fill and pulse ring share one colour via --pulse-color (see ring-pulse in index.css). */
const ACCENT_HEX: Record<string, string> = {
  blue: "#89b4fa", green: "#a6e3a1", lavender: "#b4befe",
  yellow: "#f9e2af", mauve: "#cba6f7",
};

/** Reusable menu-item button inside a popover. */
function MenuItem({
  icon,
  label,
  onClick,
  disabled,
  destructive,
  reason,
}: {
  icon: React.ReactNode;
  label: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  destructive?: boolean;
  /** Shown under the label while disabled (slice 5: disabled controls say why). */
  reason?: React.ReactNode;
}) {
  const id = useId();
  const why = disabled && reason ? reason : null;
  const base = destructive
    ? "group flex w-full items-center gap-2 rounded px-2 py-1.5 text-xs text-destructive hover:bg-destructive/10 disabled:pointer-events-none"
    : "group flex w-full items-center gap-2 rounded px-2 py-1.5 text-xs text-foreground hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none";
  return (
    <button type="button" onClick={onClick} disabled={disabled} className={base} aria-describedby={why ? id : undefined}>
      <span className={`group-disabled:opacity-50 ${destructive ? "" : "text-muted-foreground"} ${why ? "self-start mt-0.5" : ""}`}>{icon}</span>
      <span className="flex flex-1 flex-col text-left">
        <span className="group-disabled:opacity-50">{label}</span>
        {why && <ItemReason id={id}>{why}</ItemReason>}
      </span>
    </button>
  );
}

/** Reason line inside a disabled menu item; stays full contrast while the label dims. */
function ItemReason({ id, children }: { id: string; children: React.ReactNode }) {
  // aria-hidden keeps it out of the button's name; aria-describedby still reads it.
  return <span id={id} aria-hidden className="text-[11px] leading-snug text-muted-foreground">{children}</span>;
}

export function ActionBar({
  frontier,
  onFrontier,
  onFrontierWhy,
  papers,
  find,
  score,
  corpus,
  monitor,
  onSearchS2,
  searchPlaceholder = "Search Semantic Scholar…",
  isSearching,
  busy,
  busyJob,
}: ActionBarProps) {
  const [findOpen, setFindOpen] = useState(false);
  const [snowballExpanded, setSnowballExpanded] = useState(false);
  const [s2query, setS2query] = useState("");
  const [s2limit, setS2limit] = useState(50);
  const [monitorSettingsOpen, setMonitorSettingsOpen] = useState(false);
  const [scoreOpen, setScoreOpen] = useState(false);
  const [enrichOpen, setEnrichOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);

  const scoreEstimate = useRunEstimate(monitor.concept?.id, "scoring", score.unscoredCount);
  const scheduledCheck = useActivity(monitor.concept?.id, "passive").data?.running.some((j) => j.kind === "monitor");
  const monitorChecking = monitor.isMonitorRunning || !!scheduledCheck;

  const { isSnowballing, options: snowballOptions } = useSnowballStore();
  const selectedIds = useSelectionStore((s) => s.selectedIds);
  // eligibleCount needs papers — with the unified handler pattern we delegate
  // the actual papers list to the caller via onSnowball; for the threshold
  // display we read the store options but compute eligible count as 0 here
  // (full wiring happens when curate.tsx populates find.onSnowball in Task 6).
  const eligibleCount = useMemo(() => {
    return getEligibleSeeds(papers, snowballOptions.threshold).length;
  }, [papers, snowballOptions.threshold]);

  const isBusy = busy || isSnowballing;
  // Disabled buttons swallow hover, so the reason sits on a focusable wrapper (WhyDisabled).
  const busyTitle = isBusy ? `Paused while ${busy ? (busyJob ?? "another job") : "a snowball"} runs` : undefined;

  // Scoring quota (mirrors AnalyzeBar logic verbatim)
  const atLimit =
    score.scoringLimit != null && score.scoredCount >= score.scoringLimit;
  const nearLimit =
    score.scoringLimit != null && score.scoredCount >= score.scoringLimit * 0.8;
  const remaining =
    score.scoringLimit != null ? score.scoringLimit - score.scoredCount : null;

  const scoreAllWhy = atLimit ? "Your plan's scoring limit is used up."
    : score.unscoredCount === 0 ? "Every paper is scored." : null;
  const trawlWhyId = useId();
  const snowballWhyId = useId();
  const scoreAllWhyId = useId();

  function closeFind() {
    setFindOpen(false);
    setSnowballExpanded(false);
  }

  return (
    <div
      data-testid="action-bar"
      className="relative flex items-center gap-2 flex-wrap rounded-md border bg-card px-3 py-2"
    >
      {frontier && (
        <>
          <div className="flex items-center gap-2">
            <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
              Next step
            </span>
            {/* Own provider so isolated renders (tests) work, as TermHint does. */}
            <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  data-testid="frontier-btn"
                  data-accent={frontier.accent}
                  aria-description={frontier.reason}
                  onClick={onFrontier}
                  className={`inline-flex h-9 items-center gap-1.5 rounded-md px-3 text-sm font-semibold text-[#11111b] bg-[var(--pulse-color)] animate-ring-pulse`}
                  style={{ "--pulse-color": ACCENT_HEX[frontier.accent] } as CSSProperties}
                >
                  <span aria-hidden>➤</span>{frontier.label}
                </button>
              </TooltipTrigger>
              {frontier.reason && (
                <TooltipContent side="bottom" className="max-w-xs text-xs">
                  {frontier.reason}
                </TooltipContent>
              )}
            </Tooltip>
            </TooltipProvider>
            {onFrontierWhy && (
              <button
                type="button"
                data-testid="frontier-why"
                onClick={onFrontierWhy}
                className="text-xs font-medium text-primary underline decoration-dotted underline-offset-4 hover:text-foreground"
              >
                Why?
              </button>
            )}
          </div>
          <div className="mx-1 h-5 w-px bg-border" />
        </>
      )}

      {/* ── Find ▾ menu ── */}
      <WhyDisabled reason={busyTitle}>
      <Popover
        open={findOpen}
        onOpenChange={(open) => {
          setFindOpen(open);
          if (!open) setSnowballExpanded(false);
        }}
      >
        <PopoverTrigger asChild>
          <Button variant="ghost" size="sm" className="h-9 text-sm" disabled={isBusy} aria-label="Find">
            <Plus className="size-4" />
            Find
            <ChevronDown className="size-3.5" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-60 p-1.5" align="start">
          {/* ── Find papers · Semantic Scholar ── */}
          <div className="px-2 pt-1 pb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            Find papers · Semantic Scholar
          </div>

          {/* Trawl + TrawlOptionsPopover inline */}
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => { find.onTrawl(); closeFind(); }}
              disabled={isBusy || !find.hasFoundationalPapers}
              aria-describedby={!find.hasFoundationalPapers ? trawlWhyId : undefined}
              className="group flex flex-1 items-center gap-2 rounded px-2 py-1.5 text-xs text-foreground hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none"
            >
              <Network className="size-3.5 self-start mt-0.5 text-muted-foreground group-disabled:opacity-50" />
              <span className="flex flex-1 flex-col text-left">
                <span className="group-disabled:opacity-50">Trawl</span>
                <span className="text-[10px] text-muted-foreground group-disabled:opacity-50">Follow your seeds' citations</span>
                {!find.hasFoundationalPapers && <ItemReason id={trawlWhyId}>Add a seed paper first.</ItemReason>}
              </span>
            </button>
            <TrawlOptionsPopover />
          </div>

          <MenuItem
            icon={<Sparkles className="size-3.5" />}
            label="Spot Trawl"
            onClick={() => { find.onSmartTrawl(); closeFind(); }}
            disabled={isBusy}
          />

          {/* Snowball expander */}
          <button
            type="button"
            onClick={() => setSnowballExpanded((v) => !v)}
            disabled={isBusy}
            className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-xs text-foreground hover:bg-accent hover:text-accent-foreground disabled:opacity-50 disabled:pointer-events-none"
            aria-expanded={snowballExpanded}
          >
            <GitBranch className="size-3.5 text-muted-foreground" />
            <span className="flex-1 text-left">
              Snowball{" "}
              <span className="text-muted-foreground">({eligibleCount})</span>
            </span>
            <ChevronDown
              className={`size-3 transition-transform ${snowballExpanded ? "rotate-180" : ""}`}
            />
          </button>
          {snowballExpanded && (
            <div className="mt-1 space-y-2 rounded border bg-muted/30 px-2 py-2">
              <label className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">Min score</span>
                <Input
                  type="number"
                  min={0}
                  max={100}
                  value={snowballOptions.threshold}
                  onChange={(e) =>
                    useSnowballStore.getState().setOptions({
                      threshold: Number(e.target.value) || 0,
                    })
                  }
                  className="h-6 w-16 text-xs text-right"
                />
              </label>
              <label className="flex items-center gap-2 text-xs">
                <input
                  type="checkbox"
                  checked={snowballOptions.includeReferences}
                  onChange={(e) =>
                    useSnowballStore.getState().setOptions({
                      includeReferences: e.target.checked,
                    })
                  }
                  className="size-3.5 rounded border"
                />
                <span className="text-muted-foreground">Include references</span>
              </label>
              <Button
                size="xs"
                className="w-full"
                onClick={() => { find.onSnowball(); closeFind(); }}
                disabled={isBusy || eligibleCount === 0}
                aria-describedby={eligibleCount === 0 ? snowballWhyId : undefined}
              >
                <GitBranch className="size-3.5" />
                Run Snowball
              </Button>
              {eligibleCount === 0 && (
                <p id={snowballWhyId} className="text-[11px] leading-snug text-muted-foreground">
                  No papers score {snowballOptions.threshold}+. Lower Min score.
                </p>
              )}
            </div>
          )}

          <div className="my-1 h-px bg-border" />
          {/* ── Import ── */}
          <div className="px-2 pt-1 pb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            Import
          </div>

          <MenuItem
            icon={<Upload className="size-3.5" />}
            label="Import PDFs"
            onClick={() => { find.onImportPdfs(); closeFind(); }}
            disabled={isBusy}
          />
          <MenuItem
            icon={<FileText className="size-3.5" />}
            label="Import .bib"
            onClick={() => { find.onImportBibTeX(); closeFind(); }}
            disabled={isBusy}
          />
          <MenuItem
            icon={<BookOpen className="size-3.5" />}
            label="Zotero"
            onClick={() => { find.onZotero(); closeFind(); }}
            disabled={isBusy}
          />

          <div className="my-1 h-px bg-border" />
          <MenuItem
            icon={<Radio className="size-3.5" />}
            label="Run monitor"
            onClick={() => { find.onRunMonitor(); closeFind(); }}
            disabled={isBusy || monitor.isMonitorRunning}
            reason={monitor.isMonitorRunning ? "The monitor is already running." : undefined}
          />

          {(find.onStartOver || find.onUndoReset) && <div className="my-1 h-px bg-border" />}
          {find.onUndoReset && (
            <MenuItem
              icon={<RotateCcw className="size-3.5" />}
              label={find.undoResetLabel ?? "Undo last reset"}
              onClick={() => { find.onUndoReset?.(); closeFind(); }}
              disabled={isBusy}
            />
          )}
          {find.onStartOver && (
            <MenuItem
              icon={<Trash2 className="size-3.5" />}
              label="Start over…"
              onClick={() => { find.onStartOver?.(); closeFind(); }}
              disabled={isBusy}
              destructive
            />
          )}
        </PopoverContent>
      </Popover>
      </WhyDisabled>

      {/* ── Inline S2 search field — icon + input + limit, submit on Enter ── */}
      <form
        className="flex h-9 items-center gap-1.5 rounded-md border border-input bg-background px-2.5 focus-within:ring-1 focus-within:ring-ring"
        onSubmit={(e) => {
          e.preventDefault();
          if (s2query.trim()) {
            onSearchS2(s2query.trim(), s2limit);
          }
        }}
      >
        {isSearching ? (
          <Loader2 className="size-3.5 shrink-0 animate-spin text-muted-foreground" />
        ) : (
          <Search className="size-3.5 shrink-0 text-muted-foreground" />
        )}
        <input
          placeholder={searchPlaceholder}
          value={s2query}
          onChange={(e) => setS2query(e.target.value)}
          className="w-56 bg-transparent text-sm outline-none placeholder:text-muted-foreground disabled:opacity-50"
          disabled={isBusy}
        />
        <span className="h-4 w-px shrink-0 bg-border" />
        <select
          value={s2limit}
          onChange={(e) => setS2limit(Number(e.target.value))}
          disabled={isBusy}
          className="shrink-0 bg-transparent text-xs text-muted-foreground outline-none focus:outline-none disabled:opacity-50"
          title="Max results to fetch"
        >
          <option value={10}>10</option>
          <option value={25}>25</option>
          <option value={50}>50</option>
          <option value={100}>100</option>
        </select>
      </form>

      {/* ── Score ▾ menu ── */}
      <WhyDisabled reason={busyTitle}>
      <Popover open={scoreOpen} onOpenChange={setScoreOpen}>
        <PopoverTrigger asChild>
          <Button variant="ghost" size="sm" className="h-9 text-sm" disabled={isBusy} aria-label="Score">
            <BarChart3 className="size-4" />
            Score
            <ChevronDown className="size-3.5" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-60 p-1.5" align="start">
          <div className="px-2 pt-1 pb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            Score
          </div>

          {/* Score all — always shown (disabled when unscoredCount === 0 or atLimit) */}
          <button
            type="button"
            onClick={() => { score.onScoreAll(); setScoreOpen(false); }}
            disabled={score.unscoredCount === 0 || atLimit}
            aria-describedby={scoreAllWhy ? scoreAllWhyId : undefined}
            className="group flex w-full items-center gap-2 rounded px-2 py-1.5 text-xs text-foreground hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none"
          >
            <BarChart3 className={`size-3.5 text-muted-foreground group-disabled:opacity-50 ${scoreAllWhy ? "self-start mt-0.5" : ""}`} />
            <span className="flex-1 text-left">
              <span className="group-disabled:opacity-50">
              Score all{" "}
              <span className="text-muted-foreground">({score.unscoredCount})</span>
              {scoreEstimate.data?.basis === "measured" && (
                <span className="ml-1 text-[10px] text-muted-foreground">
                  {formatRange(scoreEstimate.data.low, scoreEstimate.data.high)}
                </span>
              )}
              </span>
              {scoreAllWhy && <ItemReason id={scoreAllWhyId}>{scoreAllWhy}</ItemReason>}
            </span>
            {nearLimit && remaining != null && (
              <Badge
                variant={atLimit ? "destructive" : "secondary"}
                className="ml-1 px-1 py-0 text-[10px] leading-tight"
              >
                {remaining} left
              </Badge>
            )}
          </button>
          {atLimit && score.onUpgrade && (
            <button
              type="button"
              onClick={() => { score.onUpgrade?.(); setScoreOpen(false); }}
              className="ml-7 text-[11px] font-medium text-primary underline underline-offset-2"
            >
              Upgrade
            </button>
          )}

          {/* Score selected */}
          <button
            type="button"
            onClick={() => { score.onScoreSelected(); setScoreOpen(false); }}
            disabled={selectedIds.size === 0}
            className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-xs text-foreground hover:bg-accent hover:text-accent-foreground disabled:opacity-50 disabled:pointer-events-none"
          >
            <ScanSearch className="size-3.5 text-muted-foreground" />
            <span className="flex-1 text-left">
              Score selected{" "}
              <span className="text-muted-foreground">({selectedIds.size})</span>
            </span>
          </button>

          {/* Re-score full text */}
          <button
            type="button"
            onClick={() => { score.onRescoreFullText(); setScoreOpen(false); }}
            disabled={score.needsRescoreCount === 0}
            className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-xs text-foreground hover:bg-accent hover:text-accent-foreground disabled:opacity-50 disabled:pointer-events-none"
          >
            <FileText className="size-3.5 text-muted-foreground" />
            <span className="flex-1 text-left">
              Re-score full text{" "}
              <span className="text-muted-foreground">({score.needsRescoreCount})</span>
            </span>
          </button>

          <div className="my-1 h-px bg-border" />

          {/* Reset scores — destructive */}
          <button
            type="button"
            onClick={() => { score.onResetScores(); setScoreOpen(false); }}
            disabled={score.isResetPending}
            className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-xs text-destructive hover:bg-destructive/10 disabled:opacity-50 disabled:pointer-events-none"
          >
            {score.isResetPending ? <Loader2 className="size-3.5 animate-spin" /> : <Trash2 className="size-3.5" />}
            <span className="flex-1 text-left">{score.isResetPending ? "Resetting…" : "Reset scores"}</span>
          </button>
        </PopoverContent>
      </Popover>
      </WhyDisabled>

      {/* ── Enrich ▾ menu — fill in content on papers you already have ── */}
      <WhyDisabled reason={busyTitle}>
      <Popover open={enrichOpen} onOpenChange={setEnrichOpen}>
        <PopoverTrigger asChild>
          <Button variant="ghost" size="sm" className="h-9 text-sm" disabled={isBusy} aria-label="Enrich">
            <FilePlus2 className="size-4" />
            Enrich
            <ChevronDown className="size-3.5" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-60 p-1.5" align="start">
          <div className="px-2 pt-1 pb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            Enrich papers
          </div>

          {/* Fetch open-access full-text into the corpus (filtered) */}
          <button
            type="button"
            onClick={() => { corpus.onDownloadPdfs(); setEnrichOpen(false); }}
            disabled={score.idlePdfCount === 0}
            className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-xs text-foreground hover:bg-accent hover:text-accent-foreground disabled:opacity-50 disabled:pointer-events-none"
          >
            <Download className="size-3.5 text-muted-foreground" />
            <span className="flex-1 text-left">
              Fetch full-text{" "}
              <span className="text-muted-foreground">({score.idlePdfCount})</span>
            </span>
          </button>

          {/* Fetch full-text — selected papers */}
          <button
            type="button"
            onClick={() => { corpus.onDownloadSelectedPdfs(); setEnrichOpen(false); }}
            disabled={selectedIds.size === 0}
            className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-xs text-foreground hover:bg-accent hover:text-accent-foreground disabled:opacity-50 disabled:pointer-events-none"
          >
            <Download className="size-3.5 text-muted-foreground" />
            <span className="flex-1 text-left">
              Fetch full-text — selected{" "}
              <span className="text-muted-foreground">({selectedIds.size})</span>
            </span>
          </button>

          {/* Backfill abstracts — only shown when relevant */}
          {(score.missingAbstractCount > 0 || score.backfillProgress) && (
            <button
              type="button"
              onClick={() => { score.onBackfillAbstracts(); setEnrichOpen(false); }}
              disabled={score.isBackfilling || isBusy}
              className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-xs text-foreground hover:bg-accent hover:text-accent-foreground disabled:opacity-50 disabled:pointer-events-none"
            >
              <Network className="size-3.5 text-muted-foreground" />
              <span className="flex-1 text-left">
                {score.isBackfilling && score.backfillProgress
                  ? `Backfilling (${score.backfillProgress.total - score.backfillProgress.remaining}/${score.backfillProgress.total})`
                  : `Backfill abstracts (${score.missingAbstractCount})`}
              </span>
              {score.isBackfilling && <Loader2 className="size-3 animate-spin" />}
            </button>
          )}
        </PopoverContent>
      </Popover>
      </WhyDisabled>

      {/* ── divider: active loop (left) │ endpoints (right) ── */}
      <div className="mx-1 h-5 w-px bg-border" />

      {/* ── Export ▾ menu — egress (terminal) ── */}
      <Popover open={exportOpen} onOpenChange={setExportOpen}>
        <PopoverTrigger asChild>
          <Button variant="ghost" size="sm" className="h-9 text-sm" aria-label="Export">
            <FileDown className="size-4" />
            Export
            <ChevronDown className="size-3.5" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-56 p-1.5" align="end">
          <div className="px-2 pt-1 pb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            Export corpus
          </div>
          <button
            type="button"
            onClick={() => { corpus.onExportCSV(); setExportOpen(false); }}
            className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-xs text-foreground hover:bg-accent hover:text-accent-foreground"
          >
            <FileDown className="size-3.5 text-muted-foreground" />
            <span className="flex-1 text-left">Export CSV</span>
          </button>
          <button
            type="button"
            onClick={() => { corpus.onExportBibTeX(); setExportOpen(false); }}
            className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-xs text-foreground hover:bg-accent hover:text-accent-foreground"
          >
            <FileDown className="size-3.5 text-muted-foreground" />
            <span className="flex-1 text-left">Export BibTeX</span>
          </button>
        </PopoverContent>
      </Popover>

      {/* ── Living Lit monitor popover ── */}
      {monitor.concept && (
        <Popover onOpenChange={() => setMonitorSettingsOpen(false)}>
          <PopoverTrigger asChild>
            <Button
              variant={undefined}
              size="sm"
              data-guide-target="livingLitButton"
              title={GLOSSARY.livingLit}
              className={`relative h-9 justify-center text-sm ${monitor.concept.monitor_enabled ? "border border-[#f59e0b] bg-[rgba(245,158,11,0.15)] text-[#f59e0b]" : "border border-[#f59e0b]/50 bg-[rgba(245,158,11,0.06)] text-[#f59e0b]/80 hover:bg-[rgba(245,158,11,0.12)]"}`}
            >
              {/* Pulses only while a check runs (manual, or a scheduled one the
                  Activity read reports), not merely because monitoring is on. */}
              <Radio className={`size-3.5${monitorChecking ? " animate-pulse" : ""}`} style={monitorChecking ? { animationDuration: "2s" } : undefined} />
              Living Lit
              {monitor.untriagedCount > 0 && (
                <span className="ml-1 rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-medium leading-none text-white">
                  {monitor.untriagedCount}
                </span>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80 p-0" align="start">
            {monitorSettingsOpen ? (
              <div className="p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-medium">Living Lit Settings</h4>
                  <Button
                    variant="ghost"
                    size="xs"
                    onClick={() => setMonitorSettingsOpen(false)}
                  >
                    Back
                  </Button>
                </div>
                <MonitorSettingsPanel
                  concept={monitor.concept}
                  onSettingsChange={monitor.onMonitorSettingsChange}
                  onRunNow={monitor.onRunMonitor}
                  onCancel={monitor.onCancelMonitor}
                  isSettingsUpdating={monitor.isMonitorSettingsUpdating}
                />
              </div>
            ) : (
              <div className="p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-medium">Living Lit Discoveries</h4>
                  <Button
                    variant="ghost"
                    size="xs"
                    onClick={() => setMonitorSettingsOpen(true)}
                    aria-label="Living Lit settings"
                  >
                    <Settings2 className="size-3.5" />
                  </Button>
                </div>
                <MonitorDigest
                  onRunMonitor={monitor.onRunMonitor}
                  isRunning={monitor.isMonitorRunning}
                  recentCount={monitor.monitorRecentCount}
                  highCount={monitor.monitorHighCount}
                  midCount={monitor.monitorMidCount}
                  lowCount={monitor.monitorLowCount}
                  hasMonitorRun={!!monitor.concept.last_monitor_run}
                  lastRunCount={monitor.monitorLastRunCount}
                  lastRunHighCount={monitor.monitorLastRunHighCount}
                  lastRunMidCount={monitor.monitorLastRunMidCount}
                  lastRunLowCount={monitor.monitorLastRunLowCount}
                  untriagedCount={monitor.untriagedCount}
                  onOpenTriageInbox={monitor.onOpenTriageInbox}
                />
              </div>
            )}
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}
